import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { productCategories } from '@/mocks/products';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOHelmet } from '@/components/SEOHelmet';

function HeroSection({ t, lang }: { t: (key: string) => string; lang: string }) {
  return (
    <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Vibrant%20fashion%20editorial%20flat%20lay%20with%20colorful%20sneakers%20hoodies%20and%20accessories%20scattered%20on%20a%20warm%20terracotta%20linen%20background%2C%20dramatic%20natural%20light%2C%20modern%20streetwear%20photography%2C%20rich%20warm%20tones%2C%20energetic%20composition&width=1600&height=900&seq=hero-bg-02&orientation=landscape&nocache=true"
          alt="CSSBuy Spreadsheet"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block px-4 py-1.5 bg-background-50/90 backdrop-blur-sm text-foreground-800 text-sm font-medium rounded-full mb-6">
            {t('trust_badge')}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
            {t('hero_title')}
          </h1>
          <p className="text-base md:text-lg text-background-200/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t('hero_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap text-sm"
            >
              {t('hero_cta_primary')}
            </a>
            <a
              href="#guides"
              className="px-8 py-3.5 bg-background-50/90 backdrop-blur-sm text-foreground-900 font-medium rounded-lg hover:bg-background-50 transition-all whitespace-nowrap text-sm"
            >
              {t('hero_cta_secondary')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection({ t }: { t: (key: string) => string }) {
  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
          <div>
            <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">{t('nav_guides')}</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3 mb-6">
              {t('what_is_title')}
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              {t('what_is_p1')}
            </p>
            <p className="text-foreground-600 leading-relaxed mb-6">
              {t('what_is_p2')}
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-all text-sm"
            >
              {t('what_is_cta')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-sm">
            <img
              src="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20a%20laptop%20and%20tablet%20displaying%20a%20product%20spreadsheet%20with%20folded%20fashion%20items%20beside%20them%20on%20a%20warm%20wood%20desk%2C%20natural%20light%2C%20modern%20workspace%20photography&width=800&height=600&seq=what-is-bg-02&orientation=landscape&nocache=true"
              alt="CSSBuy Spreadsheet Overview"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection({ t }: { t: (key: string) => string }) {
  const steps = [
    { icon: 'ri-folder-open-line', text: 'step_1' },
    { icon: 'ri-search-line', text: 'step_2' },
    { icon: 'ri-price-tag-3-line', text: 'step_3' },
    { icon: 'ri-shield-check-line', text: 'step_4' },
    { icon: 'ri-arrow-left-right-line', text: 'step_5' },
    { icon: 'ri-check-double-line', text: 'step_6' },
    { icon: 'ri-external-link-line', text: 'step_7' },
  ];

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">Process</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3">
            {t('how_it_works_title')}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {steps.slice(0, 4).map((step, i) => (
            <div key={step.text} className="bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300 transition-colors">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className={`${step.icon} text-primary-600 text-lg`} />
              </div>
              <span className="text-xs font-semibold text-primary-600 mb-1 block">0{i + 1}</span>
              <p className="text-sm font-medium text-foreground-800">{t(step.text)}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-4 md:mt-6 max-w-3xl mx-auto">
          {steps.slice(4).map((step, i) => (
            <div key={step.text} className="bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300 transition-colors">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className={`${step.icon} text-primary-600 text-lg`} />
              </div>
              <span className="text-xs font-semibold text-primary-600 mb-1 block">0{i + 5}</span>
              <p className="text-sm font-medium text-foreground-800">{t(step.text)}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-all text-sm"
          >
            {t('how_cta')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}

function CategoriesSection({ t, lang }: { t: (key: string) => string; lang: string }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">Categories</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3">
            {t('categories_title')}
          </h2>
          <p className="text-foreground-600 mt-3 max-w-xl mx-auto">
            {t('categories_subtitle')}
          </p>

          {/* Dropdown for categories */}
          <div className="relative inline-block mt-6">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-100 text-secondary-900 font-medium rounded-lg hover:bg-secondary-200 transition-all text-sm border border-secondary-200"
            >
              <i className="ri-grid-line" />
              {t('category_select')}
              <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-lg z-50">
                <div className="py-2 max-h-[400px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <img src={cat.imageUrl} alt={cat.name} className="w-8 h-8 rounded object-cover" />
                      <span className="text-sm text-foreground-800">
                        {lang === 'pt' ? cat.namePt : lang === 'de' ? cat.nameDe : cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => {
            const name = lang === 'pt' ? cat.namePt : lang === 'de' ? cat.nameDe : cat.name;
            return (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group block bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all hover:shadow-sm"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={cat.imageUrl}
                    alt={name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors text-center">
                    {name}
                  </p>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProductsSection({ lang }: { lang: string }) {
  const scrollRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scroll = (categoryId: string, direction: 'left' | 'right') => {
    if (!scrollRefs.current) return;
    const el = scrollRefs.current[categoryId];
    if (!el) return;
    const scrollAmount = 260;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  const titleText =
    lang === 'pt' ? 'Produtos em Destaque' : lang === 'de' ? 'Ausgewählte Produkte' : 'Featured Products';
  const subtitleText =
    lang === 'pt'
      ? 'Navegue pelos produtos da nossa coleção e clique para ver detalhes no site principal.'
      : lang === 'de'
        ? 'Durchsuchen Sie Produkte aus unserer Sammlung und klicken Sie für Details.'
        : 'Browse products from our collection and click to view details on the main site.';
  const viewAllText = lang === 'pt' ? 'Ver Todos' : lang === 'de' ? 'Alle Ansehen' : 'View All';

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">Products</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3">{titleText}</h2>
          <p className="text-foreground-600 mt-3 max-w-xl mx-auto">{subtitleText}</p>
        </div>

        {productCategories.map((cat) => {
          const catName = lang === 'pt' ? cat.namePt : lang === 'de' ? cat.nameDe : cat.name;
          const catUrl = categories.find((c) => c.id === cat.id)?.url || 'https://kaspreadsheet.com';

          return (
            <div key={cat.id} className="mb-10 last:mb-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">{catName}</h3>
                <a
                  href={catUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap"
                >
                  {viewAllText}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>

              <div className="relative group/cat">
                <button
                  type="button"
                  onClick={() => scroll(cat.id, 'left')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-background-50/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm border border-background-200/70 opacity-0 group-hover/cat:opacity-100 transition-opacity hidden md:flex"
                  aria-label="Scroll left"
                >
                  <i className="ri-arrow-left-s-line text-foreground-700" />
                </button>

                <div
                  ref={(el) => {
                    if (scrollRefs.current) scrollRefs.current[cat.id] = el;
                  }}
                  className="flex gap-3 overflow-x-auto pb-2"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {cat.items.map((product) => (
                    <a
                      key={product.id}
                      href={product.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex-shrink-0 w-[170px] md:w-[210px] group/card"
                    >
                      <div className="relative aspect-square rounded-xl overflow-hidden bg-background-200 mb-2">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <p className="text-xs text-foreground-700 leading-snug line-clamp-2 mb-1">{product.name}</p>
                      <p className="text-sm font-semibold text-primary-600">{product.price}</p>
                    </a>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => scroll(cat.id, 'right')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-background-50/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm border border-background-200/70 opacity-0 group-hover/cat:opacity-100 transition-opacity hidden md:flex"
                  aria-label="Scroll right"
                >
                  <i className="ri-arrow-right-s-line text-foreground-700" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function GuidesSection({ t }: { t: (key: string) => string }) {
  const guides = [
    { slug: 'cssbuy-review-2026', title: 'CSSBuy Review 2026', desc: 'Comprehensive review of CSSBuy features, pros, and cons.', image: 'https://readdy.ai/api/search-image?query=Five%20star%20rating%20flat%20lay%20with%20a%20smartphone%20showing%20a%20product%20review%20page%20on%20a%20warm%20wooden%20desk%2C%20soft%20natural%20light%2C%20modern%20editorial%20photography&width=800&height=500&seq=guide-review-01&orientation=landscape' },
    { slug: 'cssbuy-shipping-cost', title: 'CSSBuy Shipping Cost', desc: 'Detailed guide to shipping costs and calculation methods.', image: 'https://readdy.ai/api/search-image?query=Cardboard%20shipping%20boxes%20stacked%20with%20a%20sealed%20delivery%20package%20and%20tape%20on%20a%20neutral%20background%2C%20soft%20studio%20lighting%2C%20logistics%20concept%20photography&width=800&height=500&seq=guide-shipping-01&orientation=landscape' },
    { slug: 'cssbuy-coupon', title: 'CSSBuy Coupon', desc: 'Current coupon information and usage guide.', image: 'https://readdy.ai/api/search-image?query=A%20discount%20coupon%20tag%20with%20scissors%20and%20colorful%20shopping%20bags%20on%20a%20pastel%20background%2C%20bright%20lighting%2C%20promotional%20sale%20concept&width=800&height=500&seq=guide-coupon-01&orientation=landscape' },
    { slug: 'cssbuy-warehouse-guide', title: 'CSSBuy Warehouse', desc: 'Warehouse process, storage, and handling explained.', image: 'https://readdy.ai/api/search-image?query=Organized%20warehouse%20shelves%20with%20cardboard%20boxes%20and%20inventory%20checking%2C%20soft%20industrial%20lighting%2C%20logistics%20storage%20photography&width=800&height=500&seq=guide-warehouse-01&orientation=landscape' },
    { slug: 'cssbuy-safety-guide', title: 'CSSBuy Safety', desc: 'Safety tips and best practices for online purchasing.', image: 'https://readdy.ai/api/search-image?query=A%20padlock%20and%20shield%20icon%20on%20a%20desk%20representing%20online%20shopping%20security%2C%20soft%20lighting%2C%20trust%20and%20safety%20concept%20photography&width=800&height=500&seq=guide-safety-01&orientation=landscape' },
    { slug: 'cssbuy-faq', title: 'CSSBuy FAQ', desc: 'Frequently asked questions about CSSBuy and spreadsheets.', image: 'https://readdy.ai/api/search-image?query=A%20glowing%20question%20mark%20sign%20with%20help%20and%20support%20concept%20on%20a%20soft%20neutral%20background%2C%20minimal%20photography&width=800&height=500&seq=guide-faq-01&orientation=landscape' },
  ];

  return (
    <section id="guides" className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">Popular</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3">
            {t('guides_title')}
          </h2>
          <p className="text-foreground-600 mt-3 max-w-xl mx-auto">
            {t('guides_subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {guides.map((guide) => (
            <a
              key={guide.slug}
              href={`/article/${guide.slug}`}
              className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all hover:shadow-sm"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={guide.image}
                  alt={guide.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2">
                  {guide.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {guide.desc}
                </p>
                <span className="inline-flex items-center gap-1 text-sm text-primary-600 mt-4 group-hover:gap-2 transition-all">
                  {t('read_guide')}
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection({ t }: { t: (key: string) => string }) {
  const rows = [
    { feature: 'comparison_org', spreadsheet: 'comparison_yes', traditional: 'comparison_limited' },
    { feature: 'comparison_links', spreadsheet: 'comparison_yes', traditional: 'comparison_no' },
    { feature: 'comparison_guides', spreadsheet: 'comparison_yes', traditional: 'comparison_limited' },
    { feature: 'comparison_compare', spreadsheet: 'comparison_easy', traditional: 'comparison_manual' },
    { feature: 'comparison_intent', spreadsheet: 'comparison_yes', traditional: 'comparison_limited' },
  ];

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">Compare</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3">
            {t('comparison_title')}
          </h2>
        </div>
        <div className="overflow-hidden rounded-xl border border-background-200/70">
          <div className="grid grid-cols-3 bg-background-200/50 text-sm font-semibold text-foreground-700">
            <div className="px-4 py-3">{t('comparison_feature')}</div>
            <div className="px-4 py-3 text-center">{t('comparison_spreadsheet')}</div>
            <div className="px-4 py-3 text-center">{t('comparison_traditional')}</div>
          </div>
          {rows.map((row, i) => (
            <div key={row.feature} className={`grid grid-cols-3 text-sm ${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
              <div className="px-4 py-3.5 text-foreground-700 font-medium">{t(row.feature)}</div>
              <div className="px-4 py-3.5 text-center">
                <span className="inline-flex items-center gap-1 text-accent-600">
                  <i className="ri-check-line" />
                  {t(row.spreadsheet)}
                </span>
              </div>
              <div className="px-4 py-3.5 text-center text-foreground-500">
                {t(row.traditional)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SafetySection({ t }: { t: (key: string) => string }) {
  const tips = [
    'safety_tip_1', 'safety_tip_2', 'safety_tip_3', 'safety_tip_4',
    'safety_tip_5', 'safety_tip_6', 'safety_tip_7', 'safety_tip_8',
  ];

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
          <div className="rounded-2xl overflow-hidden shadow-sm">
            <img
              src="https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20padlock%20and%20shield%20symbol%20on%20a%20desk%20representing%20online%20shopping%20security%2C%20soft%20lighting%2C%20trust%20and%20safety%20concept%20photography&width=800&height=600&seq=safety-bg-02&orientation=landscape&nocache=true"
              alt="Safety Guide"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">Safety</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3 mb-6">
              {t('safety_title')}
            </h2>
            <div className="grid grid-cols-1 gap-3">
              {tips.map((tip) => (
                <div key={tip} className="flex items-start gap-3 p-3 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-6 h-6 flex items-center justify-center bg-accent-100 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-shield-check-line text-accent-600 text-sm" />
                  </div>
                  <p className="text-sm text-foreground-700">{t(tip)}</p>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <a
                href="/article/cssbuy-safety-guide"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-all text-sm"
              >
                {t('safety_cta')}
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ArticlesSection({ t }: { t: (key: string) => string }) {
  const articles = [
    { slug: 'cssbuy-spreadsheet-complete-guide', title: 'CSSBuy Spreadsheet Complete Guide', desc: 'Everything you need to know about using CSSBuy Spreadsheet for product research and safe purchasing.', date: '2026-08-26', time: 12 },
    { slug: 'cssbuy-review-2026', title: 'CSSBuy Review 2026', desc: 'In-depth review of CSSBuy features, services, and what buyers should know before using the platform.', date: '2026-08-24', time: 8 },
    { slug: 'cssbuy-shipping-cost', title: 'CSSBuy Shipping Cost Guide', desc: 'Understanding shipping costs, calculation methods, and how to estimate total delivery expenses.', date: '2026-08-22', time: 10 },
    { slug: 'cssbuy-coupon', title: 'CSSBuy Coupon & Discount Guide', desc: 'How to find, verify, and use coupons and discount codes when purchasing through CSSBuy.', date: '2026-08-20', time: 6 },
    { slug: 'cssbuy-safety-guide', title: 'CSSBuy Safety Guide', desc: 'Essential safety practices for verifying products, links, and sellers before making purchases.', date: '2026-08-18', time: 9 },
    { slug: 'cssbuy-warehouse-guide', title: 'CSSBuy Warehouse Process', desc: 'Understanding warehouse storage, handling, inspection, and shipping preparation processes.', date: '2026-08-16', time: 7 },
  ];

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">Latest</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3">
              {t('articles_title')}
            </h2>
          </div>
          <a href="/guides" className="hidden md:inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors">
            {t('view_all')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((article) => (
            <a
              key={article.slug}
              href={`/article/${article.slug}`}
              className="group block bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all hover:shadow-sm"
            >
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-primary-600 bg-primary-100 px-2 py-1 rounded-md font-medium">Guide</span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-3">
                  {article.desc}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground-400">{article.time} {t('reading_time')}</span>
                  <span className="inline-flex items-center gap-1 text-sm text-primary-600 group-hover:gap-2 transition-all">
                    {t('read_guide')}
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection({ t }: { t: (key: string) => string }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    { q: 'What is CSSBuy Spreadsheet?', a: 'A CSSBuy Spreadsheet is an organized collection of product links, categories, and information designed to help buyers research products more efficiently. It groups similar items together for easier browsing.' },
    { q: 'How do I use CSSBuy Spreadsheet?', a: 'Choose a category, browse the organized listings, verify product details on the external seller page, check QC information, compare options, and then proceed to purchase on the actual platform.' },
    { q: 'Where can I find product links?', a: 'Product links are organized within the spreadsheet by category. Click on any category button or use the dropdown menu to access links to the main website.' },
    { q: 'How do I verify a product?', a: 'Always visit the actual product page linked in the spreadsheet. Verify specifications, pricing, availability, and seller reputation before making any purchase.' },
    { q: 'What is QC in CSSBuy?', a: 'QC stands for Quality Check. It refers to inspection photos and reports that show the actual condition of products before they are shipped to buyers.' },
    { q: 'How is shipping cost calculated?', a: 'Shipping costs depend on destination, package weight, dimensions, and shipping method. Use the seller\'s official shipping calculator for accurate estimates.' },
    { q: 'Is CSSBuy legit?', a: 'CSSBuy is a known platform in the buyer agent space. However, users should always verify sellers, check reviews, and follow safety practices when purchasing.' },
    { q: 'How to check for coupons?', a: 'Coupon availability changes frequently. Check the official platform or trusted sources for current coupon codes and verify their validity before use.' },
  ];

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-primary-600 uppercase tracking-wider">FAQ</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3">
            {t('faq_title')}
          </h2>
          <p className="text-foreground-600 mt-3">
            {t('faq_subtitle')}
          </p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-4 md:p-5 text-left"
              >
                <span className="font-medium text-foreground-800 text-sm pr-4">{faq.q}</span>
                <i className={`ri-${openIndex === i ? 'subtract' : 'add'}-line text-primary-600 flex-shrink-0`} />
              </button>
              {openIndex === i && (
                <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed border-t border-background-200/50 pt-3">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection({ t }: { t: (key: string) => string }) {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Colorful%20shopping%20bags%20and%20fashion%20items%20arranged%20in%20a%20celebration%20composition%2C%20warm%20festive%20tones%2C%20retail%20lifestyle%20photography%2C%20wide%20banner%20composition&width=1600&height=600&seq=cta-bg-02&orientation=landscape&nocache=true"
          alt="Explore CSSBuy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
      </div>
      <div className="relative z-10 w-full px-4 md:px-8 text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
          {t('final_cta_title')}
        </h2>
        <p className="text-base md:text-lg text-background-200/90 mb-8 max-w-2xl mx-auto">
          {t('final_cta_subtitle')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap text-sm"
          >
            {t('final_cta_button')}
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-8 py-3.5 bg-background-50/90 backdrop-blur-sm text-foreground-900 font-medium rounded-lg hover:bg-background-50 transition-all whitespace-nowrap text-sm"
          >
            {t('main_cta_button')}
          </a>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHelmet
        title={t('home_seo_title')}
        description={t('home_seo_description')}
        keywords={t('home_seo_keywords')}
        pathname="/"
        lang={lang}
      />
      <Navbar />
      <main className="flex-1 pt-16">
        <HeroSection t={t} lang={lang} />
        <WhatIsSection t={t} />
        <HowItWorksSection t={t} />
        <CategoriesSection t={t} lang={lang} />
        <ProductsSection lang={lang} />
        <GuidesSection t={t} />
        <ComparisonSection t={t} />
        <SafetySection t={t} />
        <ArticlesSection t={t} />
        <FAQSection t={t} />
        <CTASection t={t} />
      </main>
      <Footer />
    </div>
  );
}